import i18n from '../../../i18n/config';
import { useTranslation } from 'react-i18next';
// arsenal-tpms/frontend/src/components/schematics/VehicleTelemetryModal/ComparisonTab.jsx
import React, { useState, useCallback, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Grid,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    Chip,
    LinearProgress,
    Stack
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    CompareArrows as CompareArrowsIcon,
    Download as DownloadIcon,
    CloudDone as CloudDoneIcon,
    Storage as StorageIcon,
    Sync as SyncIcon,
    Verified as VerifiedIcon,
    SyncProblem as SyncProblemIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Error as ErrorIcon
} from '@mui/icons-material';

import { api } from '../../../services/api';

const ComparisonTab = ({
    vehicle,
    formatSmartDateTime,
    timezoneInfo
}) => {
  const { t } = useTranslation();
    // Estados para comparación
    const [comparisonData, setComparisonData] = useState(null);
    const [comparisonLoading, setComparisonLoading] = useState(false);
    const [comparisonError, setComparisonError] = useState(null);

    // Cargar datos de comparación SIMPLIFICADA
    const loadComparisonData = useCallback(async () => {
        if (!vehicle?.id) return;

        setComparisonLoading(true);
        setComparisonError(null);

        try {
            console.log('📊 Loading simplified comparison data (Amazon vs PostgreSQL only) for vehicle:', {
                id: vehicle.id,
                name: vehicle.display_name,
                sensors: vehicle.vehicle_sensors?.length || 0
            });

            const response = await api.sync.getDataComparison({
                vehicle_id: vehicle.id,
                hours_back: 24
            });

            setComparisonData(response.data);

            console.log('📊 Simplified comparison data loaded successfully:', {
                amazon_count: response.data.summary?.amazon_count || 0,
                postgres_count: response.data.summary?.postgres_count || 0,
                sync_percentage: response.data.comparison?.sync_percentage || 0,
                comparison_type: response.data.comparison_type || 'amazon_vs_postgres_only'
            });

        } catch (error) {
            console.error('❌ Error loading simplified comparison data:', error);
            setComparisonError(error.response?.data?.detail || error.message || t('hardcoded.components_schematics_vehicletelemetrymodal_comparisontab.error_al_cargar_comparacion_de'));
        } finally {
            setComparisonLoading(false);
        }
    }, [vehicle?.id, vehicle?.display_name, vehicle?.vehicle_sensors]);

    // Función para exportar datos problemáticos
    const exportProblematicData = () => {
        const missing = comparisonData?.comparison?.missing_from_postgres || [];
        if (missing.length === 0) {
            alert(t('hardcoded.components_schematics_vehicletelemetrymodal_comparisontab.no_hay_registros_faltantes_para'));
            return;
        }

        const csvData = missing.map(timestamp => {
            const date = new Date(timestamp).toISOString();
            const amazonRecord = comparisonData.amazon_data?.find(r => r.TimeStamp === timestamp);

            return {
                [t('hardcoded.components_schematics_vehicletelemetrymodal.timestamp')]: timestamp,
                'Fecha': date,
                'ID_Amazon': amazonRecord?.id || '',
                [t('vehicles.form.type')]: amazonRecord?.type || '',
                [t('hardcoded.components_schematics_vehicletelemetrymodal.topic')]: amazonRecord?.topic || '',
                'RPSI': amazonRecord?.rpsi || '',
                'RTemp': amazonRecord?.rtemp || '',
                'Unique_ID': amazonRecord?.unique_id || '',
                [t('gps.table.status')]: 'FALTANTE_EN_POSTGRES'
            };
        });

        const headers = Object.keys(csvData[0]).join(',');
        const rows = csvData.map(row => Object.values(row).join(','));
        const csv = [headers, ...rows].join('\n');

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = t('hardcoded.components_schematics_vehicletelemetrymodal.registros_faltantes_display_name_newdatetoisostrcsv', { display_name: vehicle?.display_name, newDatetoISOStr: new Date().toISOString().split('T')[0] });
        a.click();
        window.URL.revokeObjectURL(url);
    };

    // Cargar datos al montar
    useEffect(() => {
        if (vehicle?.id) {
            loadComparisonData();
        }
    }, [vehicle?.id, loadComparisonData]);

    // Loading state
    if (comparisonLoading) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: 400,
                gap: 2
            }}>
                <CircularProgress size={60} />
                <Typography variant="h6">{t('hardcoded.components_schematics_vehicletelemetrymodal_comparisontab.cargando_comparacion_detallada')}</Typography>
            </Box>
        );
    }

    // Error state
    if (comparisonError) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                <Typography variant="h6">{t('hardcoded.components_schematics_vehicletelemetrymodal_comparisontab.error_al_cargar_comparacion_de')}</Typography>
                <Typography variant="body2">{comparisonError}</Typography>
                <Button
                    startIcon={<RefreshIcon />}
                    onClick={loadComparisonData}
                    sx={{ mt: 1 }}
                >
                    {i18n.t('hardcoded.components_ai_airecommendationspanel.reintentar')}</Button>
            </Alert>
        );
    }

    // No data state
    if (!comparisonData) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <CompareArrowsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                    {t('hardcoded.components_schematics_vehicletelemetrymodal_comparisontab.comparacion_detallada_de_datos')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('hardcoded.components_schematics_vehicletelemetrymodal_comparisontab.analiza_registros_especificos_entre_amazon')}</Typography>
                <Button
                    variant="contained"
                    startIcon={<CompareArrowsIcon />}
                    onClick={loadComparisonData}
                >
                    {t('hardcoded.components_schematics_vehicletelemetrymodal_comparisontab.cargar_comparacion_detallada')}</Button>
            </Box>
        );
    }

    const stats = comparisonData.summary || {};
    const comparison = comparisonData.comparison || {};

    return (
        <Box sx={{ p: 2, height: '100%', overflowY: 'auto' }}>
            {/* Header con estadísticas */}
            <Box sx={{ mb: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <CompareArrowsIcon color="primary" sx={{ fontSize: 32 }} />
                    <Box flex={1}>
                        <Typography variant="h6">{t('hardcoded.components_schematics_vehicletelemetrymodal_comparisontab.comparacion_detallada_de_datos')}</Typography>
                        {timezoneInfo && (
                            <Typography variant="caption" color="text.secondary">
                                {t('hardcoded.components_schematics_vehicletelemetrymodal_comparisontab.zona_horaria')}{timezoneInfo.timezone}
                            </Typography>
                        )}
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={exportProblematicData}
                            size="small"
                            disabled={!comparison.missing_from_postgres?.length}
                        >
                            {i18n.t('hardcoded.components_schematics_vehicletelemetrymodal.exportar_faltantes')}</Button>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={loadComparisonData}
                            size="small"
                        >
                            {i18n.t('audit.actions.UPDATE')}</Button>
                    </Stack>
                </Stack>

                {/* Tarjetas de estadísticas */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <CloudDoneIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
                                <Typography variant="h4" color="primary.main">
                                    {stats.amazon_count || 0}
                                </Typography>
                                <Typography variant="caption">Amazon DynamoDB</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <StorageIcon color="success" sx={{ fontSize: 32, mb: 1 }} />
                                <Typography variant="h4" color="success.main">
                                    {stats.postgres_count || 0}
                                </Typography>
                                <Typography variant="caption">{i18n.t('hardcoded.components_schematics_vehicletelemetrymodal.postgresql')}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <SyncIcon color="info" sx={{ fontSize: 32, mb: 1 }} />
                                <Typography variant="h4" color="info.main">
                                    {comparison.synced_count || 0}
                                </Typography>
                                <Typography variant="caption">{i18n.t('hardcoded.components_schematics_vehicletelemetrymodal.sincronizados')}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent sx={{ textAlign: 'center' }}>
                                {comparison.sync_percentage >= 95 ? (
                                    <VerifiedIcon color="success" sx={{ fontSize: 32, mb: 1 }} />
                                ) : (
                                    <SyncProblemIcon color="warning" sx={{ fontSize: 32, mb: 1 }} />
                                )}
                                <Typography variant="h4" color={comparison.sync_percentage >= 95 ? 'success.main' : 'warning.main'}>
                                    {comparison.sync_percentage || 0}%
                                </Typography>
                                <Typography variant="caption">{t('hardcoded.components_schematics_vehicletelemetrymodal_comparisontab.sincronizacion')}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* TABLA DETALLADA DE REGISTROS FALTANTES */}
            {comparison.missing_from_postgres && comparison.missing_from_postgres.length > 0 && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" color="warning.main" gutterBottom>
                            {t('hardcoded.components_schematics_vehicletelemetrymodal_comparisontab.registros_faltantes_en_postgresql')}{comparison.missing_from_postgres.length})
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {t('hardcoded.components_schematics_vehicletelemetrymodal_comparisontab.detalles_completos_de_cada_registro')}</Typography>

                        <TableContainer component={Paper} sx={{ maxHeight: { xs: 'calc(100vh - 320px)', sm: 600 }, overflowX: 'auto', border: 1, borderColor: 'warning.main' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ bgcolor: 'warning.main', color: 'white', fontWeight: 'bold' }}>
                                            #
                                        </TableCell>
                                        <TableCell sx={{ bgcolor: 'warning.main', color: 'white', fontWeight: 'bold', minWidth: { xs: 110, sm: 180 } }}>
                                            {i18n.t('hardcoded.components_schematics_vehicletelemetrymodal.timestamp_fecha')}</TableCell>
                                        <TableCell sx={{ bgcolor: 'warning.main', color: 'white', fontWeight: 'bold' }}>
                                            {i18n.t('hardcoded.components_schematics_vehicletelemetrymodal.id_sensor')}</TableCell>
                                        <TableCell sx={{ bgcolor: 'warning.main', color: 'white', fontWeight: 'bold' }}>
                                            {i18n.t('vehicles.form.type')}</TableCell>
                                        <TableCell sx={{ bgcolor: 'warning.main', color: 'white', fontWeight: 'bold' }}>
                                            {i18n.t('hardcoded.components_schematics_vehicletelemetrymodal.topic')}</TableCell>
                                        <TableCell sx={{ bgcolor: 'warning.main', color: 'white', fontWeight: 'bold' }}>
                                            RPSI
                                        </TableCell>
                                        <TableCell sx={{ bgcolor: 'warning.main', color: 'white', fontWeight: 'bold' }}>
                                            {i18n.t('hardcoded.components_schematics_positiontile.temp')}</TableCell>
                                        <TableCell sx={{ bgcolor: 'warning.main', color: 'white', fontWeight: 'bold' }}>
                                            Unique ID
                                        </TableCell>
                                        <TableCell sx={{ bgcolor: 'warning.main', color: 'white', fontWeight: 'bold' }}>
                                            {i18n.t('gps.table.status')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {comparison.missing_from_postgres.map((timestamp, index) => {
                                        const amazonRecord = comparisonData.amazon_data?.find(r => r.TimeStamp === timestamp) || {};
                                        const formattedDate = formatSmartDateTime(timestamp, true);

                                        return (
                                            <TableRow key={timestamp} sx={{
                                                '&:nth-of-type(odd)': { bgcolor: 'warning.50' },
                                                '&:hover': { bgcolor: 'warning.100' }
                                            }}>
                                                <TableCell sx={{ fontWeight: 'bold' }}>
                                                    {index + 1}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                                                        {timestamp}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formattedDate}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={amazonRecord.id || 'N/A'}
                                                        size="small"
                                                        variant="outlined"
                                                        color={amazonRecord.id ? 'primary' : 'default'}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={amazonRecord.type || 'N/A'}
                                                        size="small"
                                                        color={amazonRecord.type === 'PSI' ? 'info' : amazonRecord.type === 'ACC' ? 'secondary' : 'default'}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                        {amazonRecord.topic || 'N/A'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {amazonRecord.rpsi !== undefined ?
                                                        <Typography variant="body2" color="primary.main" sx={{ fontWeight: 'bold' }}>
                                                            {Number(amazonRecord.rpsi).toFixed(2)}
                                                        </Typography> :
                                                        <Typography variant="body2" color="text.disabled">--</Typography>
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    {amazonRecord.rtemp !== undefined ?
                                                        <Typography variant="body2" color="info.main" sx={{ fontWeight: 'bold' }}>
                                                            {Number(amazonRecord.rtemp).toFixed(1)}°C
                                                        </Typography> :
                                                        <Typography variant="body2" color="text.disabled">--</Typography>
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{
                                                        fontFamily: 'monospace',
                                                        fontSize: '0.75rem',
                                                        color: 'text.secondary'
                                                    }}>
                                                        {amazonRecord.unique_id ?
                                                            amazonRecord.unique_id.substring(0, 8) + '...' :
                                                            'N/A'
                                                        }
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label="FALTANTE"
                                                        size="small"
                                                        color="error"
                                                        icon={<ErrorIcon />}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>

                    </CardContent>
                </Card>
            )}

            {/* Tabla de comparación general (opcional) */}
            {comparisonData.amazon_data && comparisonData.postgres_data && (
                <Card sx={{ mb: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {t('hardcoded.components_schematics_vehicletelemetrymodal_comparisontab.vista_general_de_registros_ultimos')}</Typography>

                        <TableContainer component={Paper} sx={{ maxHeight: { xs: 'calc(100vh - 380px)', sm: 400 }, overflowX: 'auto' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                                            {i18n.t('hardcoded.components_schematics_vehicletelemetrymodal.timestamp')}</TableCell>
                                        <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                                            {t('hardcoded.components_schematics_vehicletelemetrymodal_comparisontab.en_amazon')}</TableCell>
                                        <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                                            {t('hardcoded.components_schematics_vehicletelemetrymodal_comparisontab.en_postgresql')}</TableCell>
                                        <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                                            {i18n.t('gps.table.status')}</TableCell>
                                        <TableCell sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
                                            {i18n.t('vehicles.form.type')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(() => {
                                        const allTimestamps = new Set([
                                            ...(comparisonData.amazon_data?.map(r => r.TimeStamp) || []),
                                            ...(comparisonData.postgres_data?.map(r => r.TimeStamp) || [])
                                        ]);

                                        return Array.from(allTimestamps)
                                            .sort((a, b) => b - a)
                                            .slice(0, 50)
                                            .map(timestamp => {
                                                const inAmazon = comparisonData.amazon_data?.some(r => r.TimeStamp === timestamp);
                                                const inPostgres = comparisonData.postgres_data?.some(r => r.TimeStamp === timestamp);
                                                const amazonRecord = comparisonData.amazon_data?.find(r => r.TimeStamp === timestamp);

                                                let status = 'synced';
                                                let statusColor = 'success';
                                                let statusText = 'SINCRONIZADO';
                                                let statusIcon = <CheckCircleIcon />;

                                                if (inAmazon && !inPostgres) {
                                                    status = 'missing_postgres';
                                                    statusColor = 'error';
                                                    statusText = t('hardcoded.components_schematics_vehicletelemetrymodal_comparisontab.falta_en_postgres');
                                                    statusIcon = <ErrorIcon />;
                                                } else if (!inAmazon && inPostgres) {
                                                    status = 'missing_amazon';
                                                    statusColor = 'warning';
                                                    statusText = t('hardcoded.components_schematics_vehicletelemetrymodal_comparisontab.falta_en_amazon');
                                                    statusIcon = <WarningIcon />;
                                                }

                                                const formattedDate = formatSmartDateTime(timestamp, false);

                                                return (
                                                    <TableRow key={timestamp} sx={{
                                                        bgcolor: status === 'missing_postgres' ? 'error.50' :
                                                            status === 'missing_amazon' ? 'warning.50' : 'success.50'
                                                    }}>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                                                {formattedDate}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {inAmazon ? (
                                                                <Chip label="✓" size="small" color="primary" />
                                                            ) : (
                                                                <Chip label="✗" size="small" color="default" />
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {inPostgres ? (
                                                                <Chip label="✓" size="small" color="success" />
                                                            ) : (
                                                                <Chip label="✗" size="small" color="default" />
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={statusText}
                                                                size="small"
                                                                color={statusColor}
                                                                icon={statusIcon}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {amazonRecord?.type ? (
                                                                <Chip
                                                                    label={amazonRecord.type}
                                                                    size="small"
                                                                    color={amazonRecord.type === 'PSI' ? 'info' : 'secondary'}
                                                                />
                                                            ) : (
                                                                <Typography variant="body2" color="text.disabled">--</Typography>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            });
                                    })()}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            {/* Resumen y progreso */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>{t('hardcoded.components_schematics_vehicletelemetrymodal_comparisontab.estado_de_sincronizacion')}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <LinearProgress
                            variant="determinate"
                            value={comparison.sync_percentage || 0}
                            sx={{ flex: 1, height: 12, borderRadius: 6 }}
                            color={comparison.sync_percentage >= 95 ? 'success' : 'warning'}
                        />
                        <Typography variant="h6" color="text.primary">
                            {comparison.sync_percentage || 0}%
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                        {comparison.synced_count || 0} de {comparison.total_unique_timestamps || 0} {i18n.t('hardcoded.components_schematics_vehicletelemetrymodal.registros_sincronizados_correctamente')}</Typography>
                </CardContent>
            </Card>
        </Box>
    );
};

export default ComparisonTab;